import { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '@templates/index';
import { Button, Card, Icon, Toast } from '@atoms/index';
import { AddAddressModal, ConfirmModal } from '@organisms/index';
import { addressService } from '@services/addressService';
import type { UserAddress } from '@/types';

const AddressesPage = () => {
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<UserAddress | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; address: UserAddress | null }>({ open: false, address: null });
  const [toast, setToast] = useState<{ isVisible: boolean; message: string; type: 'success' | 'error' }>({ isVisible: false, message: '', type: 'success' });

  const loadAddresses = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await addressService.getAddresses();
      setAddresses(data);
    } catch (err) {
      setError('Adresler yüklenirken bir hata oluştu');
      console.error('Load addresses error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  const openAdd = () => {
    setEditingAddress(null);
    setModalOpen(true);
  };

  const openEdit = async (address: UserAddress) => {
    try {
      const full = await addressService.getAddress(address.id);
      setEditingAddress(full);
      setModalOpen(true);
    } catch (err) {
      setToast({ isVisible: true, message: 'Adres bilgileri yüklenemedi.', type: 'error' });
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingAddress(null);
  };

  const handleModalSuccess = () => {
    loadAddresses();
    closeModal();
    setToast({ isVisible: true, message: editingAddress ? 'Adres güncellendi.' : 'Adres eklendi.', type: 'success' });
  };

  const setDefault = async (address: UserAddress) => {
    try {
      await addressService.setDefaultAddress(address.id);
      await loadAddresses();
      setToast({ isVisible: true, message: 'Varsayılan adres güncellendi.', type: 'success' });
    } catch (err) {
      setToast({ isVisible: true, message: 'Varsayılan adres ayarlanırken bir hata oluştu.', type: 'error' });
    }
  };

  const confirmDelete = (address: UserAddress) => {
    setDeleteConfirm({ open: true, address });
  };

  const handleDelete = async () => {
    if (!deleteConfirm.address) return;
    try {
      await addressService.deleteAddress(deleteConfirm.address.id);
      setDeleteConfirm({ open: false, address: null });
      await loadAddresses();
      setToast({ isVisible: true, message: 'Adres silindi.', type: 'success' });
    } catch (err) {
      setToast({ isVisible: true, message: 'Adres silinirken bir hata oluştu.', type: 'error' });
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3" />
            <div className="h-40 bg-gray-200 rounded-xl" />
            <div className="h-40 bg-gray-200 rounded-xl" />
            <div className="h-40 bg-gray-200 rounded-xl" />
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Kayıtlı Adreslerim</h1>
            <p className="text-gray-500 text-sm mt-1">Teslimat adreslerinizi yönetin</p>
          </div>
          <Button variant="primary" onClick={openAdd} className="flex items-center gap-2 shrink-0">
            <Icon name="plus" size={18} />
            Yeni Adres Ekle
          </Button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl flex items-center gap-3">
            <Icon name="close" size={20} className="text-red-500 shrink-0" />
            {error}
          </div>
        )}

        {addresses.length === 0 ? (
          <Card className="text-center py-16 px-6">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="location" size={40} className="text-primary" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Henüz kayıtlı adresiniz yok</h2>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto">
              Siparişlerinizin teslimat adresini hızlıca seçmek için adres ekleyin.
            </p>
            <Button variant="primary" size="lg" onClick={openAdd}>
              İlk Adresinizi Ekleyin
            </Button>
          </Card>
        ) : (
          <ul className="space-y-4">
            {addresses.map((address) => (
              <li key={address.id}>
                <Card className="p-5 sm:p-6 hover:shadow-md transition-shadow border border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                          <Icon name="location" size={20} className="text-primary" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">{address.title}</h3>
                          {address.isDefault && (
                            <span className="inline-block mt-0.5 px-2 py-0.5 text-xs font-medium bg-primary/15 text-primary rounded-lg">
                              Varsayılan
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-gray-700 text-sm mb-1">{address.fullAddress}</p>
                      <p className="text-gray-500 text-xs">{address.district}, {address.city}</p>
                      {address.buildingInfo && (
                        <p className="text-gray-500 text-xs mt-1">{address.buildingInfo}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!address.isDefault && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setDefault(address)}
                        >
                          Varsayılan Yap
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openEdit(address)}
                      >
                        Düzenle
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => confirmDelete(address)}
                      >
                        Sil
                      </Button>
                    </div>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}

        <AddAddressModal
          isOpen={modalOpen}
          onClose={closeModal}
          onSuccess={handleModalSuccess}
          initialAddress={editingAddress}
        />

        <ConfirmModal
          isOpen={deleteConfirm.open}
          title="Adresi sil"
          description={deleteConfirm.address
            ? `"${deleteConfirm.address.title}" adresini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`
            : ''}
          confirmText="Sil"
          cancelText="Vazgeç"
          variant="danger"
          onConfirm={handleDelete}
          onClose={() => setDeleteConfirm({ open: false, address: null })}
        />

        <Toast
          isVisible={toast.isVisible}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast((t) => ({ ...t, isVisible: false }))}
        />
      </div>
    </MainLayout>
  );
};

export default AddressesPage;
